import * as React from "react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { type Platz, type Zeiger, platziereKarte } from "./hoverPlatzierung";
import "./vorgangDetail.css";
import { alleDokumente, alleFenster, aktivesFenster } from "../../windowManager/browserFenster/fensterRegister";

/**
 * Vorschau-Karte, die beim Überfahren am Zeiger aufgeht (PO 30.08.2026,
 * Platzierung überarbeitet 31.08.2026).
 *
 * Vier Dinge, die eine Hover-Karte richtig machen muss:
 *  · VERZÖGERUNG. Ohne sie flackert bei jedem Überstreichen einer Liste eine
 *    Karte auf — und bei Dokumenten zöge jedes Vorbeiziehen eine Datei vom
 *    Server. `inhalt` wird deshalb erst gerufen, wenn die Karte wirklich aufgeht.
 *  · TASTATURWEG. Hover allein schließt aus, wer nicht zeigen kann; Fokus öffnet
 *    sie ebenso, Escape schließt sie.
 *  · KEIN CLIPPING. Als Portal an `body`, `position: fixed` — in einer Liste mit
 *    `overflow:auto` wäre sie sonst abgeschnitten.
 *  · IHRE ECHTE HÖHE. Sie wird gemessen, nicht geschätzt. Die frühere Annahme
 *    „220 px" stimmte für eine Mail-Kopfzeile, nicht für eine PDF-Seite; stand
 *    die Zeile unten im Fenster, lief die Karte unten aus dem Bild. Weil Bild,
 *    PDF-Seite und Mailtext ERST NACH dem Öffnen eintreffen, misst ein
 *    ResizeObserver nach und rückt die Karte zurecht.
 *  · KLICK IST ÖFFNEN DES GANZEN (T-40). Wer die Karte anklickt, öffnet die
 *    Mail bzw. das Dokument im eigenen Fenster — die Vorschau hat dann ihren
 *    Zweck erfüllt und darf nicht darüber liegen bleiben. Der Klick schließt
 *    sie, bricht eine noch laufende Verweildauer ab und sperrt das Öffnen, bis
 *    der Zeiger den Anker verlassen hat. Fokus öffnet nur noch beim
 *    Tastaturweg (`:focus-visible`) — sonst ginge sie nach dem Klick über den
 *    Fokus gleich wieder auf, oder wenn das Fenster beim Schließen den Fokus
 *    zurückgibt. Verliert das Browserfenster den Fokus, schließt sie ebenso.
 *
 * Die Karte fängt selbst keine Mausereignisse (`pointer-events: none`): sie ist
 * zum Ansehen da, nicht zum Anfassen. So kann sie das Element, das sie erklärt,
 * nie überdecken und einen Klick schlucken.
 */

type Props = {
    /** Inhalt der Karte — erst beim Öffnen aufgerufen (lazy). */
    inhalt: () => React.ReactNode;
    /** Verweildauer, bevor die Karte aufgeht. */
    verzoegerungMs?: number;
    breite?: number;
    /** Zusatzklasse des Ankers — er sitzt IN der Zeile und muss deren Layout mitmachen. */
    ankerClass?: string;
    children: React.ReactNode;
};

/** Nur die erste Schätzung, bis wirklich gemessen ist. */
const HOEHE_VOR_DEM_MESSEN = 240;

/**
 * Kam der Fokus über die Tastatur? Browser sagen es über `:focus-visible`;
 * kennt die Umgebung den Selektor nicht, entscheidet der Rückfallwert.
 */
const istTastaturFokus = (el: Element | null, rueckfall: boolean): boolean => {
    if (!el || typeof el.matches !== "function") return rueckfall;
    try {
        return el.matches(":focus-visible");
    } catch {
        return rueckfall;
    }
};

export const HoverKarte: React.FC<Props> = ({ inhalt, verzoegerungMs = 350, breite = 320, ankerClass, children }) => {
    const [offen, setOffen] = useState(false);
    const [platz, setPlatz] = useState<Platz | null>(null);
    const ankerRef = useRef<HTMLSpanElement | null>(null);
    const karteRef = useRef<HTMLDivElement | null>(null);
    const timer = useRef<number | null>(null);
    /** Wo der Zeiger gerade ist. */
    const zeigerRef = useRef<Zeiger | null>(null);
    /**
     * Wo er beim Öffnen WAR. Die Karte bleibt an diesem Punkt hängen; liefe sie
     * dem Zeiger nach, spränge sie beim Nachmessen jedes Mal woanders hin.
     */
    const ankerZeiger = useRef<Zeiger | null>(null);
    /**
     * Nach einem Klick gesperrt, bis der Zeiger den Anker verlässt (T-40):
     * die Mail ist dann im eigenen Fenster offen, die Vorschau läge darüber.
     */
    const gesperrt = useRef(false);

    const abbrechen = useCallback(() => {
        if (timer.current) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    }, []);

    const merkeZeiger = useCallback((e: React.MouseEvent) => {
        zeigerRef.current = { x: e.clientX, y: e.clientY };
    }, []);

    const platziere = useCallback(() => {
        const anker = ankerRef.current;
        if (!anker) return;
        const karte = karteRef.current;
        const r = anker.getBoundingClientRect();
        // `scrollHeight` statt `offsetHeight`: die Höhe, die der Inhalt WILL,
        // auch wenn die Karte gerade gedeckelt ist. Mit der gedeckelten Höhe
        // zu rechnen hieße, den Deckel als Messwert zu nehmen. (+2 für die
        // Rahmenlinien, die `scrollHeight` nicht mitzählt.)
        const hoehe = karte ? Math.max(karte.scrollHeight, karte.offsetHeight) + 2 : HOEHE_VOR_DEM_MESSEN;
        const naechster = platziereKarte({
            zeiger: ankerZeiger.current,
            anker: { top: r.top, left: r.left, right: r.right, bottom: r.bottom },
            breite,
            hoehe,
            fenster: { breite: aktivesFenster().innerWidth, hoehe: aktivesFenster().innerHeight },
        });
        // Nur bei echter Änderung neu setzen — sonst dreht sich der
        // Messen-Setzen-Messen-Kreis des ResizeObservers endlos.
        setPlatz((alt) =>
            alt &&
            alt.top === naechster.top &&
            alt.left === naechster.left &&
            alt.maxHoehe === naechster.maxHoehe &&
            alt.nachOben === naechster.nachOben
                ? alt
                : naechster,
        );
    }, [breite]);

    const oeffne = useCallback(() => {
        abbrechen();
        if (gesperrt.current) return;
        timer.current = window.setTimeout(() => {
            timer.current = null;
            if (gesperrt.current) return;
            ankerZeiger.current = zeigerRef.current;
            setOffen(true);
        }, verzoegerungMs);
    }, [abbrechen, verzoegerungMs]);

    const schliesse = useCallback(() => {
        abbrechen();
        setOffen(false);
        setPlatz(null);
    }, [abbrechen]);

    /** Klick auf den Anker: schließen und bis zum Verlassen nicht wieder öffnen. */
    const sperreUndSchliesse = useCallback(() => {
        gesperrt.current = true;
        schliesse();
    }, [schliesse]);

    useEffect(() => () => abbrechen(), [abbrechen]);

    // Messen und platzieren, BEVOR der Browser zeichnet — sonst blitzt die
    // Karte einen Frame lang an der falschen Stelle auf.
    useLayoutEffect(() => {
        if (!offen) return;
        platziere();
        const karte = karteRef.current;
        if (!karte || typeof ResizeObserver === "undefined") return;
        const beobachter = new ResizeObserver(() => platziere());
        beobachter.observe(karte);
        return () => beobachter.disconnect();
    }, [offen, platziere]);

    useEffect(() => {
        if (!offen) return;
        // Wechselt man das Browserfenster oder den Tab, gehört die Karte
        // nicht mehr zu dem, was man gerade ansieht.
        const onSichtbarkeit = () => {
            if (document.hidden) schliesse();
        };
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") schliesse();
        };
        // Scrollt die Liste unter der Karte weg, wandert die Karte nicht mit —
        // sie gehört zu einer Zeile, die dann nicht mehr dort steht.
        alleDokumente.addEventListener("keydown", onKey);
        alleFenster.addEventListener("scroll", schliesse, true);
        alleFenster.addEventListener("resize", schliesse);
        window.addEventListener("blur", schliesse);
        document.addEventListener("visibilitychange", onSichtbarkeit);
        return () => {
            alleDokumente.removeEventListener("keydown", onKey);
            alleFenster.removeEventListener("scroll", schliesse, true);
            alleFenster.removeEventListener("resize", schliesse);
            window.removeEventListener("blur", schliesse);
            document.removeEventListener("visibilitychange", onSichtbarkeit);
        };
    }, [offen, schliesse]);

    return (
        <>
            <span
                ref={ankerRef}
                className={`vgd-hover-anker${ankerClass ? ` ${ankerClass}` : ""}`}
                onMouseEnter={(e) => {
                    merkeZeiger(e);
                    oeffne();
                }}
                onMouseMove={merkeZeiger}
                onMouseLeave={() => {
                    gesperrt.current = false;
                    schliesse();
                }}
                // Capture-Phase: die Karte ist zu, bevor `onClick` der Zeile
                // das Mail-/Dokumentfenster öffnet. `click` zusätzlich, weil
                // Enter/Leertaste auf einem Button `click` ohne `pointerdown` auslöst.
                onPointerDownCapture={sperreUndSchliesse}
                onMouseDownCapture={sperreUndSchliesse}
                onClickCapture={sperreUndSchliesse}
                onFocusCapture={(e) => {
                    // Nur der Tastaturweg öffnet über den Fokus — ein Mausklick
                    // fokussiert ebenfalls, soll aber die Vorschau nicht öffnen.
                    if (gesperrt.current || !istTastaturFokus(e.target as Element, true)) return;
                    // Fokus ohne Zeiger: die Karte hängt sich an den Anker.
                    zeigerRef.current = null;
                    oeffne();
                }}
                onBlurCapture={() => {
                    // Fokus ist weg (z. B. ins geöffnete Fenster): die Sperre
                    // hat ihren Dienst getan. Sonst bliebe sie für jemanden,
                    // der nur mit der Tastatur arbeitet, nie mehr gelöst.
                    gesperrt.current = false;
                    schliesse();
                }}
            >
                {children}
            </span>
            {offen &&
                createPortal(
                    <div
                        ref={karteRef}
                        className="vgd vgd-hoverkarte"
                        role="tooltip"
                        style={{
                            // Vor dem ersten Messen liegt die Karte zwar im
                            // Baum (anders ließe sie sich nicht messen), ist
                            // aber unsichtbar — sonst sähe man sie kurz oben
                            // links kleben.
                            top: platz ? platz.top : 0,
                            left: platz ? platz.left : 0,
                            width: breite,
                            maxHeight: platz ? platz.maxHoehe : undefined,
                            visibility: platz ? undefined : "hidden",
                        }}
                    >
                        {inhalt()}
                    </div>,
                    document.body,
                )}
        </>
    );
};
